// ── BCH Live — Super Chat ─────────────────────────────────────────
//
// Minimal taps: pick amount → send. The BCH transaction is what the user
// is waiting on, so the UI confirms as soon as the tx is broadcast and
// publishes the Nostr receipt + chat message in the background.
//
// The wallet and the creator's BCH address are both preloaded by the
// viewer page, so tapping "Super Chat" is usually instant.

import React, { useState, useEffect, useRef } from 'react';
import { X, Check, Loader2, Coins, Sparkles } from 'lucide-react';
import { useAppContext } from '../../hooks/useAppContext';
import { useNostrPublish } from '../../hooks/useNostrPublish';
import { useNostrProfile } from '../../hooks/useNostrQuery';
import { useTipWallet } from '../../hooks/useTipWallet';
import { prefetchBCHAddress, getCachedBCHAddress } from '../../hooks/useBCHAddress';
import { getBCHPrice } from '../../hooks/useBCHPrice';
import { LIVE_CHAT_KIND, slog, swarn } from '../../lib/streamCredentials';
import { TIP_RECEIPT_KIND } from '../../hooks/useStreamTips';
import { formatUsd, tierColor } from './StreamOverlays';

const PRESET_USD = [0.05, 0.25, 1, 5, 10];

/** Tier name shown next to the amount, so the ladder is legible. */
function tierName(usd: number): string {
  if (usd >= 10) return 'Legendary';
  if (usd >= 5) return 'Epic';
  if (usd >= 1) return 'Big';
  if (usd >= 0.25) return 'Nice';
  return 'Cheers';
}

export interface SuperChatModalProps {
  streamerPubkey: string;
  aTag: string;
  onClose: () => void;
  onSent: () => void;
}

export default function SuperChatModal({
  streamerPubkey, aTag, onClose, onSent,
}: SuperChatModalProps) {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const { profile: myProfile } = useNostrProfile(user?.pubkey);
  const tipWallet = useTipWallet();

  const [selectedUsd, setSelectedUsd] = useState(0.25);
  const [customUsd, setCustomUsd] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Usually already done by the page-level preload; harmless if repeated.
  useEffect(() => {
    if (!tipWallet.ready) tipWallet.init().catch(() => {});
    prefetchBCHAddress(streamerPubkey);
    // Intentionally once on open — re-running on wallet state churn would
    // fire redundant init() calls mid-send.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamerPubkey]);

  // Escape closes, matching every other modal in the app.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && status !== 'sending') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, status]);

  const activeUsd = useCustom ? (parseFloat(customUsd) || 0) : selectedUsd;
  const price = getBCHPrice();
  const activeSats = price ? Math.round((activeUsd / price) * 1e8) : 0;
  const canAfford = tipWallet.balanceSats >= BigInt(activeSats) + BigInt(300);
  const canSend = activeUsd >= 0.01 && activeSats > 0 && canAfford && status === 'idle';
  const color = tierColor(activeUsd);

  const handleSend = async () => {
    if (!user || !canSend) return;
    setStatus('sending');
    try {
      if (!tipWallet.ready) await tipWallet.init();
      let addr = getCachedBCHAddress(streamerPubkey);
      if (!addr) addr = await prefetchBCHAddress(streamerPubkey);
      if (!addr) {
        setErrorMsg('Creator has no BCH address');
        setStatus('error');
        setTimeout(() => setStatus('idle'), 3000);
        return;
      }

      // The BCH transaction is the part that matters. sendTipWithFee already
      // does optimistic balance updates internally.
      const txid = await tipWallet.sendTipWithFee(addr, BigInt(activeSats));
      setStatus('sent');
      slog('superchat', `sent ${formatUsd(activeUsd)} (${activeSats} sats) txid=${String(txid).slice(0, 12)}`);

      // Nostr publishes are fire-and-forget: the money already moved, so
      // never let a slow relay hold up the confirmation the user sees.
      const now = Math.floor(Date.now() / 1000);
      Promise.all([
        publish({
          kind: TIP_RECEIPT_KIND,
          content: JSON.stringify({
            amount_sats: activeSats,
            amount_usd: activeUsd,
            txid: txid || '',
            message: message.trim() || undefined,
          }),
          tags: [
            ['p', streamerPubkey],
            ['a', aTag],
            ['amount', String(activeSats)],
            ['t', 'bch-tip'],
            ['t', 'superchat'],
          ],
          created_at: now,
        }),
        message.trim()
          ? publish({
              kind: LIVE_CHAT_KIND,
              content: `💚 ${formatUsd(activeUsd)} Super Chat: ${message.trim()}`,
              tags: [['a', aTag, '', 'root']],
              created_at: now + 1,
            })
          : Promise.resolve(null),
      ]).catch(err => swarn('superchat', 'background publish failed', err));

      setTimeout(() => { onSent(); onClose(); }, 700);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Send failed');
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
      onClick={() => status !== 'sending' && onClose()}
      role="dialog"
      aria-modal="true"
      aria-label="Send a Super Chat"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      <div className="sc-modal-enter relative mx-auto w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="overflow-hidden rounded-t-3xl border border-border bg-card shadow-2xl sm:rounded-3xl">
          {/* Header */}
          <div className="flex items-center justify-between px-5 pb-3 pt-5">
            <h2 className="flex items-center gap-2 text-base font-extrabold">
              <Sparkles className="h-4 w-4 text-emerald-500" />
              Super Chat
            </h2>
            <button
              onClick={onClose}
              aria-label="Close"
              className="rounded-full p-1.5 transition-colors hover:bg-muted"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <p className="px-5 pb-3 text-[11px] leading-relaxed text-muted-foreground">
            Instant Bitcoin Cash, straight to the creator. No middleman, no
            payout delay, no chargebacks.
          </p>

          {/* Amounts */}
          <div className="px-5 pb-4">
            <div className="grid grid-cols-5 gap-2">
              {PRESET_USD.map(amt => {
                const active = !useCustom && selectedUsd === amt;
                return (
                  <button
                    key={amt}
                    onClick={() => { setSelectedUsd(amt); setUseCustom(false); }}
                    className={`rounded-xl py-3 text-center text-sm font-bold transition-all active:scale-95 ${
                      active
                        ? 'scale-105 text-white shadow-lg'
                        : 'bg-muted text-foreground hover:bg-muted/80'
                    }`}
                    style={active ? { backgroundColor: tierColor(amt) } : undefined}
                  >
                    {formatUsd(amt)}
                  </button>
                );
              })}
            </div>

            <div className="mt-3">
              {useCustom ? (
                <div
                  className="flex items-center gap-2 rounded-xl border-2 bg-muted px-4 py-3 transition-colors"
                  style={{ borderColor: color }}
                >
                  <span className="text-sm font-bold text-muted-foreground">$</span>
                  <input
                    ref={inputRef}
                    type="number"
                    value={customUsd}
                    onChange={e => setCustomUsd(e.target.value)}
                    placeholder="0.00"
                    step="0.01"
                    min="0.01"
                    aria-label="Custom amount in USD"
                    className="flex-1 bg-transparent text-sm font-bold [appearance:textfield] focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    autoFocus
                  />
                  <button
                    onClick={() => { setUseCustom(false); setCustomUsd(''); }}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => { setUseCustom(true); setTimeout(() => inputRef.current?.focus(), 100); }}
                  className="w-full rounded-xl border border-dashed border-border py-2.5 text-xs font-medium text-muted-foreground transition-colors hover:border-primary hover:text-primary"
                >
                  Custom amount
                </button>
              )}
            </div>

            {activeUsd >= 0.01 && activeSats > 0 && (
              <p className="mt-2 text-center text-[11px] text-muted-foreground">
                ≈ {activeSats.toLocaleString()} sats
                <span className="mx-1.5 opacity-40">·</span>
                <span className="font-bold" style={{ color }}>{tierName(activeUsd)}</span>
              </p>
            )}
          </div>

          {/* Message */}
          <div className="px-5 pb-4">
            <input
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Add a message (optional)"
              maxLength={200}
              aria-label="Super Chat message"
              className="w-full rounded-xl bg-muted px-4 py-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          {/* Preview */}
          {activeUsd >= 0.01 && (
            <div
              className="mx-5 mb-4 rounded-xl border-2 p-3"
              style={{ borderColor: color, backgroundColor: `${color}10` }}
            >
              <div className="flex items-center gap-2">
                {myProfile?.picture ? (
                  <img src={myProfile.picture} alt="" className="h-6 w-6 rounded-full object-cover" />
                ) : (
                  <div className="h-6 w-6 rounded-full bg-muted" />
                )}
                <span className="truncate text-xs font-bold">
                  {myProfile?.display_name || myProfile?.name || 'You'}
                </span>
                <span
                  className="ml-auto flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-black text-white"
                  style={{ backgroundColor: color }}
                >
                  <Coins className="h-3 w-3" />
                  {formatUsd(activeUsd)}
                </span>
              </div>
              {message && (
                <p className="mt-1.5 break-words text-xs text-foreground/85">{message}</p>
              )}
            </div>
          )}

          {/* Send */}
          <div className="safe-area-bottom px-5 pb-5">
            <button
              onClick={handleSend}
              disabled={!canSend}
              className={`w-full rounded-2xl py-4 text-base font-bold transition-all active:scale-[0.98] disabled:opacity-50 ${
                status === 'sent' ? 'bg-emerald-500 text-white'
                  : status === 'error' ? 'bg-red-500 text-white'
                  : 'bg-emerald-500 text-white hover:bg-emerald-600'
              }`}
            >
              {status === 'sending' ? (
                <Loader2 className="mx-auto h-5 w-5 animate-spin" />
              ) : status === 'sent' ? (
                <span className="flex items-center justify-center gap-2">
                  <Check className="h-5 w-5" /> Sent!
                </span>
              ) : status === 'error' ? (
                errorMsg.slice(0, 40)
              ) : (
                `Send ${formatUsd(activeUsd)}`
              )}
            </button>
            {!canAfford && activeUsd >= 0.01 && status === 'idle' && (
              <p className="mt-1.5 text-center text-[10px] font-semibold text-amber-500">
                Insufficient balance — top up your tip wallet in Settings
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
